import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { attendantsService, Attendant, ConversationAssignment } from '../services/attendantsService';

interface AttendantAssignmentProps {
  conversationId: string;
  onAssignmentChange?: (assignment: ConversationAssignment | null) => void;
  compact?: boolean; // Modo compacto para exibição na lista de chats
}

const AttendantAssignment: React.FC<AttendantAssignmentProps> = ({
  conversationId,
  onAssignmentChange,
  compact = false,
}) => {
  const [assignment, setAssignment] = useState<ConversationAssignment | null>(null);
  const [attendants, setAttendants] = useState<Attendant[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [assigning, setAssigning] = useState(false);

  // Carregar atribuição atual e lista de atendentes
  useEffect(() => {
    // Evitar nova tentativa se já deu erro (previne loop ao dar erro de API)
    if (loadError) return;

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const [assignmentData, attendantsData] = await Promise.all([
          attendantsService.getAssignment(conversationId),
          attendantsService.getAll(true), // Apenas ativos - usa cache/deduplicação
        ]);
        if (cancelled) return;
        setAssignment(assignmentData);
        setAttendants(attendantsData);
        setLoadError(false);
      } catch (error) {
        if (cancelled) return;
        // Setar erro para não continuar tentando em loop
        setLoadError(true);
        // Só logar no console uma vez, não repetidamente
        console.warn('[AttendantAssignment] Falha ao carregar atendentes. O recurso será ocultado até recarregar a página.', conversationId);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => { cancelled = true; };
  }, [conversationId]); // loadError intencionalmente fora das deps para não re-executar ao setar erro

  // Abrir menu
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  // Fechar menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Atribuir a um atendente
  const handleAssign = async (attendantId: string) => {
    try {
      setAssigning(true);
      const newAssignment = await attendantsService.assignConversation(
        conversationId,
        attendantId
      );
      setAssignment(newAssignment);
      onAssignmentChange?.(newAssignment);
      handleClose();
    } catch (error: any) {
      console.error('Erro ao atribuir:', error);
      if (error.response?.data?.already_assigned) {
        alert(error.response.data.error);
      }
    } finally {
      setAssigning(false);
    }
  };

  // Remover atribuição
  const handleRemove = async () => {
    try {
      setAssigning(true);
      await attendantsService.removeAssignment(conversationId);
      setAssignment(null);
      onAssignmentChange?.(null);
      handleClose();
    } catch (error) {
      console.error('Erro ao remover atribuição:', error);
    } finally {
      setAssigning(false);
    }
  };

  // Modo compacto (para lista de chats)
  if (compact) {
    // Não renderizar nada em caso de erro ou carregamento para não poluir a lista
    if (loadError || loading) return null;
    if (!assignment) return null;

    return (
      <Tooltip title={`Atribuído a: ${assignment.attendant?.name}`}>
        <Chip
          size="small"
          avatar={
            <Avatar
              sx={{
                bgcolor: assignment.attendant?.color || '#666',
                width: 20,
                height: 20,
                fontSize: '0.7rem',
              }}
            >
              {assignment.attendant?.name?.charAt(0).toUpperCase()}
            </Avatar>
          }
          label={assignment.attendant?.name}
          sx={{
            height: 24,
            fontSize: '0.75rem',
            '& .MuiChip-avatar': {
              width: 20,
              height: 20,
            },
          }}
        />
      </Tooltip>
    );
  }

  // Modo normal (para dentro do chat)
  // Se deu erro ao carregar (ex: tabela não existe), não mostrar nada
  if (loadError) return null;

  return (
    <Box>
      {loading ? (
        <CircularProgress size={20} />
      ) : assignment ? (
        // Atribuído - mostrar atendente
        <Tooltip title="Clique para alterar atribuição">
          <Chip
            onClick={handleClick}
            avatar={
              <Avatar
                sx={{
                  bgcolor: assignment.attendant?.color || '#666',
                }}
              >
                {assignment.attendant?.name?.charAt(0).toUpperCase()}
              </Avatar>
            }
            label={assignment.attendant?.name}
            onDelete={handleRemove}
            deleteIcon={
              assigning ? (
                <CircularProgress size={16} />
              ) : (
                <PersonRemoveIcon />
              )
            }
            sx={{
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          />
        </Tooltip>
      ) : (
        // Não atribuído - mostrar botão
        <Tooltip title="Atribuir atendente">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.light',
              },
            }}
          >
            <PersonAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Menu de seleção */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Atribuir a:
          </Typography>
        </Box>
        <Divider />
        {attendants.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="Nenhum atendente disponível" />
          </MenuItem>
        ) : (
          attendants.map((attendant) => (
            <MenuItem
              key={attendant.id}
              onClick={() => handleAssign(attendant.id)}
              disabled={assigning || assignment?.attendant_id === attendant.id}
              selected={assignment?.attendant_id === attendant.id}
            >
              <ListItemIcon>
                <Avatar
                  sx={{
                    bgcolor: attendant.color,
                    width: 28,
                    height: 28,
                    fontSize: '0.875rem',
                  }}
                >
                  {attendant.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemIcon>
              <ListItemText primary={attendant.name} />
              {assignment?.attendant_id === attendant.id && (
                <CheckIcon fontSize="small" color="primary" />
              )}
            </MenuItem>
          ))
        )}
        {assignment && (
          <>
            <Divider />
            <MenuItem onClick={handleRemove} disabled={assigning}>
              <ListItemIcon>
                <PersonRemoveIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Remover atribuição"
                primaryTypographyProps={{ color: 'error' }}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default AttendantAssignment;
